import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AddImage } from "../../api/UploadImage/UploadImg";
import { toast } from "react-toastify";

interface SpreadsheetFileUploadProps {
  onFileUploaded: (fileUrl: string) => void;
  onCancel: () => void;
}

const ACCEPTED = ".xlsx,.xls,.csv";

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SpreadsheetFileUpload = ({
  onFileUploaded,
  onCancel,
}: SpreadsheetFileUploadProps) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: uploadFile, isPending } = useMutation({
    mutationFn: (formData: FormData) => {
      return AddImage(
        formData,
        "response_option_answers",
        (event) => {
          const progress = Math.round((event.loaded * 90) / event.total);
          setUploadProgress(progress);
        },
        false
      );
    },
    onSuccess: (response) => {
      setUploadProgress(100);
      toast.success("File uploaded successfully!");
      onFileUploaded(response.url);
    },
    onError: (error: any) => {
      console.error("Upload error:", error);
      toast.error("Failed to upload file");
      setUploadProgress(0);
    },
  });

  const acceptFile = (file?: File | null) => {
    if (!file) return;
    // Guard the drop path: the file input enforces `accept`, dragging does not.
    const isAllowed = ACCEPTED.split(",").some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );
    if (!isAllowed) {
      toast.error("Only .xlsx, .xls or .csv files are accepted");
      return;
    }
    setSelectedFile(file);
    setUploadProgress(0);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    uploadFile(formData);
  };

  const buttonBase: React.CSSProperties = {
    padding: "0.7rem 1.5rem",
    borderRadius: "8px",
    border: "none",
    fontSize: "0.95rem",
    fontWeight: 600,
  };

  return (
    <div style={{ padding: "1.75rem", width: "560px", maxWidth: "90vw" }}>
      <p
        style={{
          fontSize: "0.9rem",
          color: "#475569",
          marginBottom: "1.25rem",
          lineHeight: 1.6,
        }}
      >
        Upload the spreadsheet you filled in. Your previous upload for this
        question is replaced.
      </p>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          acceptFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => !isPending && inputRef.current?.click()}
        style={{
          border: `2px dashed ${isDragging ? "#4379EE" : "#cbd5e1"}`,
          background: isDragging ? "#eff4ff" : "#f8fafc",
          borderRadius: "12px",
          padding: "2.75rem 1.5rem",
          textAlign: "center",
          marginBottom: "1.25rem",
          cursor: isPending ? "not-allowed" : "pointer",
          transition: "border-color 0.15s ease, background 0.15s ease",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          onChange={(e) => acceptFile(e.target.files?.[0])}
          accept={ACCEPTED}
          style={{ display: "none" }}
        />
        {selectedFile ? (
          <>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: "#1e293b",
                wordBreak: "break-all",
              }}
            >
              {selectedFile.name}
            </div>
            <div
              style={{
                marginTop: "0.35rem",
                fontSize: "0.85rem",
                color: "#64748b",
              }}
            >
              {formatSize(selectedFile.size)} — click to choose a different file
            </div>
          </>
        ) : (
          <>
            <div
              style={{ fontSize: "1rem", fontWeight: 600, color: "#334155" }}
            >
              Drag your file here, or click to browse
            </div>
            <div
              style={{
                marginTop: "0.35rem",
                fontSize: "0.85rem",
                color: "#94a3b8",
              }}
            >
              Accepts .xlsx, .xls and .csv
            </div>
          </>
        )}
      </div>

      {uploadProgress > 0 && (
        <div style={{ marginBottom: "1.25rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "0.8rem",
              color: "#64748b",
              marginBottom: "0.4rem",
            }}
          >
            <span>{uploadProgress === 100 ? "Done" : "Uploading..."}</span>
            <span>{uploadProgress}%</span>
          </div>
          <div
            style={{
              width: "100%",
              height: "8px",
              backgroundColor: "#e2e8f0",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                backgroundColor: "#4379EE",
                width: `${uploadProgress}%`,
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>
      )}

      <div
        style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}
      >
        <button
          onClick={onCancel}
          disabled={isPending}
          style={{
            ...buttonBase,
            backgroundColor: "#fff",
            color: "#475569",
            border: "1px solid #cbd5e1",
            cursor: isPending ? "not-allowed" : "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleUpload}
          disabled={!selectedFile || isPending}
          style={{
            ...buttonBase,
            backgroundColor: !selectedFile || isPending ? "#cbd5e1" : "#4379EE",
            color: "#fff",
            cursor: !selectedFile || isPending ? "not-allowed" : "pointer",
          }}
        >
          {isPending ? "Uploading..." : "Upload & Submit"}
        </button>
      </div>
    </div>
  );
};

export default SpreadsheetFileUpload;
